import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faStar } from "@fortawesome/free-solid-svg-icons";
import {
  convertMonthsToReadableFormat,
  getInitialsImage,
} from "src/helpers/helperFunctions";
import CartIcon from "src/assets/images/website-images/calendar-date.png";
import { IconButton } from "@mui/material";
import IconFav from "src/assets/images/heart-favorite.png";
import AddCartButton from "src/Components/GlobalComponents/AddCartButton";
import { Skeleton } from "@mui/material";
import PrimaryBtn from "src/Components/GlobalComponents/primaryBtn";
import { useNavigate } from "react-router-dom";
import { CURRENCY } from "src/lib/constans";
import FavoriteCoponent from "src/Components/GlobalComponents/FavoriteCoponent";
import FavStarIcon from "src/Components/GlobalComponents/FavStarIcon";

interface Doctor {
  id: string;
  name: string;
  specialty: string[];
  qualification: string;
  experience: string;
  reviews: number;
  rating: number;
  price: number;
  currency: string;
  image: string;
  isFavorite: boolean;
}

interface DoctorCardProps {
  doctor: Doctor;
  skeleton?: boolean;
  onLikeProduct?: (id: string, isFavorite: boolean) => void;
}

const DoctorViewCard: React.FC<DoctorCardProps> = ({
  doctor,
  skeleton = false,
  onLikeProduct,
}) => {
  const navigate = useNavigate();

  return (
    <div className="bg-doctors-image relative rounded-[10px] border border-[#305e5a] shadow-[0px_0px_8px_#00000029] p-3 lg:p-[0.8vw] flex items-center justify-between w-full">
      {skeleton ? (
        <>
          <div className="flex w-[30%] min-w-[200px]">
            <Skeleton
              variant="rectangular"
              width={60}
              height={60}
              sx={{ borderRadius: 2, minWidth: "6vw", minHeight: "6vw" }}
            />
            <div className="ml-2 flex flex-col gap-1 w-full overflow-hidden">
              <Skeleton variant="text" width="40%" height={24} />
              <Skeleton variant="text" width="60%" height={20} />
              <Skeleton variant="text" width="50%" height={20} />
            </div>
          </div>
          <div className="flex flex-1 items-end justify-center">
            <div className="flex gap-5 lg:gap-7 2xl:gap-9">
              <div className="flex flex-col border-r border-[#B2B2B2] pr-5 lg:pr-7 2xl:pr-9">
                <Skeleton variant="text" width={50} height={16} />
                <Skeleton variant="text" width={80} height={20} />
              </div>
              <div className="flex flex-col">
                <Skeleton variant="text" width={40} height={16} />
                <Skeleton variant="text" width={30} height={20} />
              </div>
              <div className="flex flex-col border-l border-[#B2B2B2] pl-5 lg:pl-7 2xl:pl-9">
                <Skeleton variant="text" width={40} height={16} />
                <Skeleton variant="rectangular" width={60} height={20} />
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2 justify-end">
            <Skeleton variant="text" width={70} height={20} />
            <Skeleton
              variant="rectangular"
              width={120}
              height={36}
              sx={{ borderRadius: 50, ml: 1 }}
            />
          </div>
        </>
      ) : (
        <>
          <div className="absolute top-2 right-2">
            <FavoriteCoponent
              id={doctor?.id}
              liked={doctor?.isFavorite}
              onLikeProduct={(id, isFavorite) => {
                onLikeProduct?.(id, isFavorite);
              }}
              category="doctor"
            />
          </div>
          <div className="flex w-[30%] mr-3 lg:mr-[0.8vw]">
            <img
              src={`${doctor?.image || ""}`}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = getInitialsImage(doctor?.name, 150, 48, "#00443F");
              }}
              alt={doctor?.name}
              className="w-[6vw] min-w-[6vw] rounded-lg object-cover aspect-square"
            />
            <div className="ml-2 flex flex-col gap-1 overflow-hidden">
              <h3 className="font-size-23px font-Montserrat-SemiBold text-[#00443F] truncate capitalize">
                {doctor?.name}
              </h3>
              <p className="text-[#202020] font-size-16px font-Poppins-Medium overflow-hidden">
                <span className="line-clamp-2 leading-tight">
                  {doctor?.specialty?.slice(0, 2).join(" | ") || "Neurologist"}
                </span>
              </p>
              <p className="text-[#393939] truncate block max-w-[15vw] font-size-16px font-Poppins-Regular">
                {doctor?.qualification || "MBBS, FCPS (Neurologist)"}
              </p>
            </div>
          </div>
          <div className="flex flex-1 items-end justify-center">
            <div className="flex gap-2  lg:gap-[2vw] justify-end">
              <div className="flex flex-col border-r relative border-[#B2B2B2] pr-5 lg:pr-7 2xl:pr-9">
                <span className="text-[#707070] absolute font-Poppins-Regular font-size-14px">
                  Experience
                </span>
                <span className="text-[#2A2A2A] opacity-0 font-Poppins-Medium font-size-16px leading-tight">
                  11 years 08 months
                </span>
                <span className="text-[#2A2A2A] font-Poppins-Medium font-size-16px">
                  {convertMonthsToReadableFormat(
                    Number(doctor?.experience || 0)
                  )}
                </span>
              </div>
              <div className="flex flex-col">
                <span className="text-[#707070] absolute font-Poppins-Regular font-size-14px">
                  Reviews
                </span>
                <span className="text-[#2A2A2A] opacity-0 font-Poppins-Medium font-size-16px leading-tight">
                  Reviews
                </span>
                <span className="text-[#2A2A2A]  font-Poppins-Medium font-size-16px">
                  {doctor?.reviews || 0}
                </span>
              </div>
              <div className="flex flex-col border-l border-[#B2B2B2] pl-5 lg:pl-7 2xl:pl-9">
                <span className="text-[#707070] font-Poppins-Regular font-size-14px">
                  Rating
                </span>
                <FavStarIcon rating={doctor?.rating || 0} size="small" />
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2 justify-end self-end ml-4 lg:ml-[2.5vw]">
            {doctor?.price > 0 && <p className="flex gap-2 items-center">
              <span className="font-size-16px font-Montserrat-Regular text-[#202020]">
                Fee:
              </span>
              <span className="text-[#00443F] whitespace-nowrap font-Montserrat-SemiBold font-size-18px">
                {doctor?.currency || CURRENCY}{" "}
                {doctor?.price?.toLocaleString() || 0}
              </span>
            </p>}
            <PrimaryBtn
              ButtonText="Book Appointment"
              style={{
                borderRadius: "50px",
                backgroundColor: "#00443F",
                color: "white",
                whiteSpace: "nowrap",
              }}
              onClick={() => {
                navigate(`/doctor-public-profile/${doctor?.id}/view`);
              }}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default DoctorViewCard;
